import React, { useState, useRef, useEffect } from 'react';
import DomainResultCard from './DomainResultCard';
import Markdown from "react-markdown";
import SearchBar from './SearchBar';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  toolCalls?: Array<{
    name: string;
    arguments: any;
    response?: any;
  }>;
  searchResults?: Array<{
    title: string;
    description: string;
    url: string;
    image_url: string;
    type: string;
    domain: string;
  }>;
}

interface AgenticChatWindowProps {
  messages: Message[];
  isLoading: boolean;
  onSendMessage: (message: string) => void;
}

const AgenticChatWindow: React.FC<AgenticChatWindowProps> = ({ messages, isLoading, onSendMessage }) => {
  const [showSearchingIndicator, setShowSearchingIndicator] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout>();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Handle loading indicator timing
  useEffect(() => {
    if (isLoading) {
      loadingTimeoutRef.current = setTimeout(() => {
        setShowSearchingIndicator(true);
      }, 1000);
    } else {
      setShowSearchingIndicator(false);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    }

    return () => {
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [isLoading]);

  return (
    <div className="flex flex-col h-full w-full" style={{ backgroundColor: 'var(--rhea-bg-primary)' }}>
      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => {
          return (
            <div
              key={`${index}-${message.content}`}
              className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} w-full`}
            >
              <div
                className={`inline-block max-w-[85%] rounded-xl px-4 py-2 ${
                  message.role === 'user'
                    ? 'bg-blue-500 text-white'
                    : message.searchResults
                    ? 'bg-inherit border'
                    : 'bg-inherit'
                }`}
                style={{
                  backgroundColor: message.role === 'user' ? 'var(--rhea-message-user)' : 'var(--rhea-message-system)',
                  borderColor: message.searchResults ? 'var(--rhea-card-border)' : 'transparent'
                }}
              >
                <div className="prose dark:prose-invert max-w-none break-words [&_strong]:text-inherit" style={{ color: 'var(--rhea-text-primary)' }}>
                  <Markdown>{message.content}</Markdown>
                </div>
                
                {/* Render search results if present */}
                {message.searchResults && message.searchResults.length > 0 && (
                  <div className="mt-4">
                    <div className="flex overflow-x-auto gap-4 pb-4 snap-x snap-mandatory" style={{ minHeight: '200px' }}>
                      {message.searchResults.map((result, idx) => (
                        <div key={idx} className="snap-center shrink-0 w-48 flex-shrink-0">
                          <DomainResultCard
                            title={result.title}
                            description={result.description}
                            url={result.url}
                            image_url={result.image_url}
                            type={result.type}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
        {showSearchingIndicator && (
          <div className="flex justify-start">
            <div className="bg-inherit rounded-lg p-3 animate-pulse" style={{ borderColor: 'var(--rhea-card-border)' }}>
              <div className="text-sm" style={{ color: 'var(--rhea-text-primary)' }}>
                Searching for you...
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <div className="flex-none p-4 border-t" style={{ borderColor: 'var(--rhea-card-border)' }}>
        <SearchBar
          onSearch={onSendMessage}
          isLoading={isLoading}
          placeholderMessage="Ask Rhea anything..."
          autoFocus={true}
          deleteOnSubmit={true}
        />
      </div>
    </div>
  );
};

export default AgenticChatWindow;